import { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { HOWITWORKS } from "@/constants/testIds";
import { useFees, pct, money } from "@/hooks/useFees";
import {
  Search, Palette, Truck, PackageOpen,
  Upload, Calculator, Coins, ArrowRight,
} from "lucide-react";

const FLOWS = {
  buy: [
    { icon: Search, title: "Discover", desc: "Browse thousands of ready-made designs from makers everywhere." },
    { icon: Palette, title: "Customize", desc: "Pick your material, color and finish \u2014 preview it instantly." },
    { icon: PackageOpen, title: "A maker prints it", desc: "A verified maker near you prints your order on their own machine." },
    { icon: Truck, title: "Delivered", desc: "It ships straight to your door, tracked every mile." },
  ],
  sell: [
    { icon: Upload, title: "Upload", desc: "Drop in your 3D model and list it in the marketplace in minutes." },
    { icon: Calculator, title: "Set your price", desc: "Use the built-in price calculator to price it right and preview your earnings." },
    { icon: Truck, title: "You ship it", desc: "Payments and shipping are all done for you." },
    { icon: Coins, title: "Get paid", desc: "Earn on every sale as buyers discover and order your prints." },
  ],
};

// Interactive price-calculator preview shown in the seller flow. Uses the live
// print-shop-125 fee policy: transaction fee + processing (% + fixed).
const PriceCalculator = () => {
  const [price, setPrice] = useState(45);
  const { fees } = useFees();

  const round2 = (x) => Math.round(x * 100 + 1e-6) / 100;
  const transactionFee = round2(price * fees.transaction_rate);
  const processingFee = round2(price * fees.processing_rate + fees.processing_fixed);
  const totalFees = round2(transactionFee + processingFee);
  const earn = Math.max(0, round2(price - totalFees));

  return (
    <div className="mx-auto mt-10 max-w-lg rounded-2xl border border-[rgba(212,0,255,0.35)] bg-card/70 p-6 backdrop-blur-md shadow-[var(--shadow-elev-1)]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calculator className="h-4 w-4 text-neo-magenta" />
          <span className="font-display text-sm font-semibold text-foreground">
            Price calculator
          </span>
        </div>
        <span className="rounded-full border border-border/70 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wide text-muted-foreground">
          {fees.connected ? "Live fees" : "Estimate"}
        </span>
      </div>

      <div className="mt-5">
        <div className="flex items-end justify-between">
          <label htmlFor="calc-price" className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
            Your list price
          </label>
          <span className="font-display text-2xl font-semibold text-foreground">
            {money(price)}
          </span>
        </div>
        <input
          id="calc-price"
          data-testid="calc-price-slider"
          type="range"
          min="5"
          max="120"
          step="1"
          value={price}
          onChange={(e) => setPrice(Number(e.target.value))}
          className="mt-3 h-2 w-full cursor-pointer appearance-none rounded-full bg-secondary accent-[var(--neo-magenta)]"
          aria-label="Your list price"
        />
      </div>

      <div className="mt-5 space-y-2">
        <div className="flex items-center justify-between rounded-xl border border-border/60 bg-secondary/30 px-3 py-2.5">
          <span className="font-mono text-[11px] text-muted-foreground">
            Transaction fee ({pct(fees.transaction_rate)})
          </span>
          <span data-testid="calc-transaction-fee" className="font-mono text-sm font-semibold text-foreground">
            -{money(transactionFee)}
          </span>
        </div>
        <div className="flex items-center justify-between rounded-xl border border-border/60 bg-secondary/30 px-3 py-2.5">
          <span className="font-mono text-[11px] text-muted-foreground">
            Processing ({pct(fees.processing_rate)} + {money(fees.processing_fixed)})
          </span>
          <span data-testid="calc-processing-fee" className="font-mono text-sm font-semibold text-foreground">
            -{money(processingFee)}
          </span>
        </div>
        <div className="flex items-center justify-between rounded-xl border border-[rgba(0,229,255,0.4)] bg-[rgba(0,229,255,0.08)] px-3 py-2.5">
          <span className="font-mono text-[11px] text-neo-cyan">You earn / sale</span>
          <span data-testid="calc-you-earn" className="font-display text-lg font-semibold text-foreground">
            {money(earn)}
          </span>
        </div>
      </div>

      <p className="mt-4 font-mono text-[10px] text-muted-foreground">
        {fees.listing_fee === 0 ? "No listing fees." : `${money(fees.listing_fee)} listing fee.`} Fees pulled live from the Printopia marketplace.
      </p>
    </div>
  );
};

export const HowItWorks = ({ onScrollTo }) => {
  const [mode, setMode] = useState("buy");
  const reduce = useReducedMotion();
  const steps = FLOWS[mode];

  return (
    <section
      id="how-it-works"
      data-testid={HOWITWORKS.section}
      className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24"
    >
      <Reveal className="text-center">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-neo-magenta">
          How Printopia works
        </span>
        <h2 className="mx-auto mt-3 max-w-2xl font-display text-3xl font-semibold tracking-tight sm:text-4xl">
          Getting (and selling) prints has never been this easy
        </h2>
      </Reveal>

      {/* Toggle */}
      <Reveal delay={0.05}>
        <div className="mx-auto mt-8 flex w-full max-w-xs items-center rounded-full border border-border/70 bg-secondary/40 p-1">
          <button
            data-testid={HOWITWORKS.buyerTab}
            onClick={() => setMode("buy")}
            className={`relative flex-1 rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
              mode === "buy" ? "text-background" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {mode === "buy" && (
              <motion.span
                layoutId="how-toggle"
                className="absolute inset-0 rounded-full bg-[var(--neo-cyan)]"
                transition={{ type: "tween", duration: 0.25, ease: "easeOut" }}
              />
            )}
            <span className="relative">I want to buy</span>
          </button>
          <button
            data-testid={HOWITWORKS.sellerTab}
            onClick={() => setMode("sell")}
            className={`relative flex-1 rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
              mode === "sell" ? "text-background" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {mode === "sell" && (
              <motion.span
                layoutId="how-toggle"
                className="absolute inset-0 rounded-full bg-[var(--neo-magenta)]"
                transition={{ type: "tween", duration: 0.25, ease: "easeOut" }}
              />
            )}
            <span className="relative">I want to sell</span>
          </button>
        </div>
      </Reveal>

      {/* Steps */}
      <div className="relative mt-12">
        <div className="pointer-events-none absolute left-0 right-0 top-7 hidden h-px bg-gradient-to-r from-transparent via-border to-transparent lg:block" />
        <AnimatePresence mode="wait">
          <motion.div
            key={mode}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -10 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4"
          >
            {steps.map((s, i) => (
              <motion.div
                key={s.title}
                initial={reduce ? { opacity: 0 } : { opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 }}
                className="group relative rounded-2xl border border-border/70 bg-card/70 p-5 backdrop-blur-md transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-[rgba(0,229,255,0.4)] hover:shadow-[var(--shadow-elev-2)]"
              >
                <div className="relative z-10 mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-border/70 bg-background/60">
                  <s.icon
                    className={mode === "buy" ? "h-6 w-6 text-neo-cyan" : "h-6 w-6 text-neo-magenta"}
                  />
                  <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-background font-mono text-[11px] font-semibold text-foreground ring-1 ring-border">
                    {i + 1}
                  </span>
                </div>
                <h3 className="mt-4 text-center font-display text-base font-semibold text-foreground">
                  {s.title}
                </h3>
                <p className="mt-2 text-center text-sm leading-relaxed text-muted-foreground">
                  {s.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Price calculator (seller flow only) */}
      {mode === "sell" && <PriceCalculator />}

      <Reveal delay={0.1} className="mt-10 text-center">
        <button
          onClick={() => onScrollTo?.("download")}
          className="inline-flex items-center gap-2 rounded-[var(--radius)] border border-[rgba(0,229,255,0.4)] bg-[rgba(0,229,255,0.16)] px-5 py-3 text-sm font-medium text-foreground shadow-[var(--glow-cyan)] transition-[background-color,box-shadow] duration-200 hover:bg-[rgba(0,229,255,0.24)]"
        >
          Download the App
          <ArrowRight className="h-4 w-4" />
        </button>
      </Reveal>
    </section>
  );
};
